const buildTableCartScopeKey = (tableId, isDelivery = false) =>
  `${isDelivery ? "delivery" : "table"}:${String(tableId ?? "")}`;

const isSameOrderItemId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

export const createTableCartSyncTracker = () => {
  const generations = new Map();
  const readSequences = new Map();
  const clearGrants = new Map();

  const getGeneration = (tableId, { isDelivery = false } = {}) => {
    if (tableId === null || tableId === undefined || tableId === "") return 0;
    return Number(generations.get(buildTableCartScopeKey(tableId, isDelivery))) || 0;
  };

  const bumpGeneration = (tableId, { isDelivery = false } = {}) => {
    if (tableId === null || tableId === undefined || tableId === "") return 0;
    const key = buildTableCartScopeKey(tableId, isDelivery);
    const generation = (Number(generations.get(key)) || 0) + 1;
    generations.set(key, generation);
    clearGrants.delete(key);
    return generation;
  };

  const beginRead = (tableId, { isDelivery = false } = {}) => {
    const key = buildTableCartScopeKey(tableId, isDelivery);
    const sequence = (Number(readSequences.get(key)) || 0) + 1;
    readSequences.set(key, sequence);
    return {
      key,
      tableId: String(tableId ?? ""),
      isDelivery: Boolean(isDelivery),
      sequence,
      generation: getGeneration(tableId, { isDelivery }),
    };
  };

  const beginFreeValidation = (
    tableId,
    { isDelivery = false, eventGeneration = null } = {},
  ) => {
    const currentGeneration = getGeneration(tableId, { isDelivery });
    const parsedEventGeneration = Number(eventGeneration);
    if (
      eventGeneration !== null &&
      eventGeneration !== undefined &&
      Number.isFinite(parsedEventGeneration) &&
      parsedEventGeneration === currentGeneration
    ) {
      return currentGeneration;
    }
    return bumpGeneration(tableId, { isDelivery });
  };

  const isCurrentRead = (token) =>
    Boolean(
      token &&
        Number(readSequences.get(token.key)) === Number(token.sequence) &&
        getGeneration(token.tableId, { isDelivery: token.isDelivery }) ===
          Number(token.generation),
    );

  const grantClear = (
    tableId,
    { isDelivery = false, generation = null, reason = "confirmed-empty" } = {},
  ) => {
    const key = buildTableCartScopeKey(tableId, isDelivery);
    const expectedGeneration =
      generation === null
        ? getGeneration(tableId, { isDelivery })
        : Number(generation);
    const grant = {
      key,
      tableId: String(tableId ?? ""),
      isDelivery: Boolean(isDelivery),
      generation: expectedGeneration,
      reason,
    };
    clearGrants.set(key, grant);
    return grant;
  };

  const hasClearGrant = (tableId, { isDelivery = false } = {}) => {
    const key = buildTableCartScopeKey(tableId, isDelivery);
    const grant = clearGrants.get(key);
    return Boolean(
      grant &&
        Number(grant.generation) === getGeneration(tableId, { isDelivery }),
    );
  };

  const consumeClearGrant = (tableId, { isDelivery = false } = {}) => {
    const key = buildTableCartScopeKey(tableId, isDelivery);
    if (!hasClearGrant(tableId, { isDelivery })) return null;
    const grant = clearGrants.get(key);
    clearGrants.delete(key);
    return grant;
  };

  const revokeClear = (tableId, { isDelivery = false } = {}) => {
    clearGrants.delete(buildTableCartScopeKey(tableId, isDelivery));
  };

  return {
    beginFreeValidation,
    beginRead,
    bumpGeneration,
    consumeClearGrant,
    getGeneration,
    grantClear,
    hasClearGrant,
    isCurrentRead,
    revokeClear,
  };
};

export { buildTableCartScopeKey, isSameOrderItemId };
